import { Accordion, Card, Input } from '@tecsinapse/cortex-react'
import React, { useEffect, useState } from 'react'
import { Funcionalidade, Produto, TipoAcesso } from '@/core/types/graphql'
import { OptionSelect } from '@/core/utils/types'
import { TabelaFuncionalidades } from './tabela-funcionalidades'
import { TriggerAccordion } from './trigger-accordion'
import {
  filterFuncionalidadeByTipoAcesso,
  handleChangeTipoAcesso,
  normalizeText
} from '../../utils/utils'
import { useForm, useFormContext, useWatch } from 'react-hook-form'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

interface ProdutoPermissaoExpansionPanelProps {
  produto: Produto
  setProdutos: React.Dispatch<React.SetStateAction<Produto[]>>
  onOpen: () => void
  tourId?: string
  onOpenTour?: () => void
}

const CardProdutoPermissao = (props: ProdutoPermissaoExpansionPanelProps) => {
  const { produto, tourId, onOpenTour, onOpen, setProdutos } = props

  const { funcionalidades, tiposAcesso } = produto

  const { setValue } = useFormContext()
  const values = useWatch()

  const [selectedTiposAcessos, setSelectedTiposAcessos] = useState<
    OptionSelect[]
  >([])
  const [selectedFuncionalidades, setSelectedFuncionalidades] = useState<
    Funcionalidade[]
  >([])

  const { register, control } = useForm({ defaultValues: { search: '' } })
  const { search } = useWatch({ control })

  useEffect(() => {
    const ids = values?.produtos[produto.id]?.funcionalidades || []
    const aux = ids.map((outterId: string) =>
      (funcionalidades ?? []).find(
        funcionalidade => funcionalidade?.id === outterId
      )
    )
    setSelectedFuncionalidades(aux as Funcionalidade[])
    setSelectedTiposAcessos(values?.produtos[produto.id]?.idsTiposAcesso || [])
  }, [funcionalidades, produto, values?.produtos])

  const updateSelectedTiposAcesso = (newTiposAcesso: OptionSelect[]) => {
    setValue(`produtos.${produto.id}.idsTiposAcesso`, newTiposAcesso, {
      shouldDirty: true
    })
    setSelectedTiposAcessos(newTiposAcesso)
  }

  const removeFuncionalidade = (funcionalidade: Funcionalidade) => {
    const newFuncionalidades = selectedFuncionalidades.filter(
      func => func.id !== funcionalidade.id
    )
    const funcionalidadesIds = newFuncionalidades
      .filter(Boolean)
      .map(func => func.id)
    setValue(`produtos.${produto.id}.funcionalidades`, funcionalidadesIds, {
      shouldDirty: true
    })
    setSelectedFuncionalidades(newFuncionalidades)
  }

  const addFuncionalidade = (funcionalidade: Funcionalidade) => {
    const newFuncionalidades = [...selectedFuncionalidades, funcionalidade]
    const funcionalidadesIds = newFuncionalidades
      .filter(Boolean)
      .map(func => func.id)

    setValue(`produtos.${produto.id}.funcionalidades`, funcionalidadesIds, {
      shouldDirty: true
    })
    setSelectedFuncionalidades(newFuncionalidades)
  }

  const updateSelectedFuncionalidades = (
    newFuncionalidades: Funcionalidade[]
  ) => {
    const currentSelected = selectedFuncionalidades ?? []
    const filteredDataIds = data.map(funcionalidade => funcionalidade.id)

    const selectedOutsideFilter = currentSelected.filter(
      funcionalidade => !filteredDataIds.includes(funcionalidade?.id)
    )

    const mergedFuncionalidades = [
      ...selectedOutsideFilter,
      ...newFuncionalidades
    ]
    const funcionalidadeIds = mergedFuncionalidades
      .filter(Boolean)
      .map(funcionalidade => funcionalidade.id)

    setValue(`produtos.${produto.id}.funcionalidades`, funcionalidadeIds, {
      shouldDirty: true
    })
    setSelectedFuncionalidades(mergedFuncionalidades)
  }

  const data = filterFuncionalidadeByTipoAcesso(
    selectedTiposAcessos,
    funcionalidades as Funcionalidade[]
  ).filter(f => normalizeText(f.nome).includes(normalizeText(search)))

  return (
    <>
      <Accordion.Face direction={'vertical'}>
        <Card className={'px-0 border space-y-deca'}>
          <TriggerAccordion
            tourId={tourId}
            produto={produto}
            setProdutos={setProdutos}
            onOpen={onOpen}
            selectedTiposAcessos={selectedTiposAcessos}
            onChangeTipoAcesso={(selectedTiposIds: OptionSelect[]) => {
              handleChangeTipoAcesso(
                selectedTiposIds,
                selectedTiposAcessos,
                selectedFuncionalidades,
                tiposAcesso as TipoAcesso[],
                funcionalidades as Funcionalidade[],
                updateSelectedFuncionalidades,
                updateSelectedTiposAcesso
              )
            }}
            onOpenTour={onOpenTour}
          />
          <Accordion.Content
            direction={'vertical'}
            key={selectedFuncionalidades.length}
          >
            <div className={'flex w-full flex-col flex-1'}>
              {selectedTiposAcessos.length > 0 ? (
                <>
                  <div className='px-6'>
                    <div className='flex justify-between items-center mb-6'>
                      <p className='font-bold'>
                        {t`Selecione as permissões do usuário abaixo:`}
                      </p>

                      <div className='w-1/3'>
                        <Input.Search
                          label={t`Buscar permissão...`}
                          placeholder={t`Digite o nome da permissão`}
                          {...register('search')}
                        />
                      </div>
                    </div>
                  </div>

                  <TabelaFuncionalidades
                    data={data}
                    updateSelectedFuncionalidades={
                      updateSelectedFuncionalidades
                    }
                    addFuncionalidade={addFuncionalidade}
                    removeFuncionalidade={removeFuncionalidade}
                    selectedFuncionalidades={selectedFuncionalidades}
                    tourId={tourId}
                  />
                </>
              ) : (
                <div
                  className={
                    'font-bold h-full items-center justify-center flex'
                  }
                >
                  <p>
                    <Trans>
                      Selecione um tipo acesso para visualizar as
                      funcionalidades
                    </Trans>
                  </p>
                </div>
              )}
            </div>
          </Accordion.Content>
        </Card>
      </Accordion.Face>
    </>
  )
}

export default CardProdutoPermissao
